import * as Sentry from '@sentry/nextjs';
import { atom } from 'jotai';

import { DTOFileInfo } from 'src/apis/dto/DTOFileInfo';
import AfterMatchModal from 'src/components/AzarOnly/AfterMatchModal';
import { type AfterMatchPopupEventParam } from 'src/components/AzarOnly/AfterMatchModal/events';
import { userDataAtom } from 'src/stores/auth/atoms';
import { ReportMatchAPI } from 'src/stores/match/apis';
import {
  MLMetricsAtom,
  chatStartDateAtom,
  matchInfoAtom,
  statusAtom,
  validateImageAndReportMatchAtom,
} from 'src/stores/match/atoms';
import { antmanFeaturesCapturedAtom, hvmFeaturesCapturedAtom } from 'src/stores/ml/atoms';
import { DETECTION_SOURCE, MLFeatureType, MLModelType } from 'src/stores/ml/types';
import { closeModalAtom, showModalAtom } from 'src/stores/modal/atoms';
import {
  hvsScreeningCaptureAtom,
  underageTriggerContextAtom,
} from 'src/stores/moderation/underageAutoReportAtoms';
import { showToastAtom } from 'src/stores/toast/atoms';
import { ReportMatchMethod, STATUS } from 'src/types/Match';
import { ModalType } from 'src/types/Modal';
import { ToastType } from 'src/types/Toast';
import { ControlledPromise } from 'src/utils/controlled-promise';

type AfterMatchAction = 'REPORT' | 'CANCEL';

export type AfterMatchPopupData =
  | { source: 'hvm'; file: Blob; triggeredAt: number }
  | { source: 'hvs'; fileInfo?: DTOFileInfo };

/**
 * 팝업 노출 조건을 평가하여 데이터를 반환하는 read-only atom.
 * cleanup 전(atom RESET 전)에 get()으로 읽어야 유효한 값을 얻는다.
 *
 * HVM on-device 캡처 또는 HVS ScreeningEvent DETECTED+VICTIM 상태를 기반으로
 * 팝업 표시 데이터를 반환한다. HVS 이벤트가 있으면 우선한다 (서버 판단 기반).
 * HVS fileInfo는 있을 때만 첨부하고, 없어도 신고는 가능하다.
 */
export const afterMatchPopupCaptureDataAtom = atom((get): AfterMatchPopupData | null => {
  const status = get(statusAtom);
  if (status !== STATUS.MATCHED) return null;

  // peer가 미성년자 허용 화이트리스트인 경우 skip (handleUnderageTriggerAtom과 동일)
  if (get(matchInfoAtom)?.peerProfile?.isUnderAgeAllowed) return null;

  // 블러 UI가 노출되었거나 In-Match 자동신고가 이미 완료된 경우
  const peerBlurred = get(antmanFeaturesCapturedAtom)[MLFeatureType.MatchBlur];
  const underageReported = get(underageTriggerContextAtom).state.kind === 'Report';
  if (peerBlurred || underageReported) return null;

  // 매치 시간이 autoReportDurationLimit 초과
  const chatStartDate = get(chatStartDateAtom);
  const userData = get(userDataAtom);
  const durationLimitMs = userData?.featureOptions?.autoReportDurationLimitMs;
  if (chatStartDate && durationLimitMs != null) {
    const matchDurationMs = Date.now() - chatStartDate.getTime();
    if (matchDurationMs > durationLimitMs) return null;
  }

  // localFrameAfterMatchPopupDisabled — 현재 peer 프레임만 캡처하므로 무조건 pass
  // TODO: mirror(local) 캡처 추가 시 RC 체크 + videoFlowType 판별 필요

  // HVS 서버 screening 결과가 있으면 우선 사용
  const matchId = get(matchInfoAtom)?.matchId;
  const hvsCapture = get(hvsScreeningCaptureAtom);
  if (hvsCapture && hvsCapture.matchId === matchId) {
    return { source: 'hvs', fileInfo: hvsCapture.fileInfo ?? undefined };
  }

  // HVM on-device 캡처 결과
  const captureData = get(hvmFeaturesCapturedAtom)[MLFeatureType.AfterMatchHVReportPopupMinor];
  if (!captureData) return null;

  return { source: 'hvm', file: captureData.file, triggeredAt: captureData.triggeredAt };
});

interface AfterMatchPopupContext {
  matchId: string;
  requestId: string;
  captureData: AfterMatchPopupData;
  reconnect?: boolean;
}
/**
 * After Match Report Popup을 표시하고 유저 액션(신고/취소)을 처리한다.
 * handleDisconnectAtom에서 cleanup + 화면 전환 후 호출.
 */
export const handleAfterMatchPopupAtom = atom(
  null,
  async (get, set, { matchId, requestId, reconnect, captureData }: AfterMatchPopupContext) => {
    const controlledPromise = new ControlledPromise<AfterMatchAction>();
    const eventParams: AfterMatchPopupEventParam = {
      popupCategory: 'fakeadult',
      screenName: reconnect ? 'MatchFinding' : 'Mirror',
      detection_source:
        captureData.source === 'hvm' ? DETECTION_SOURCE.ON_DEVICE : DETECTION_SOURCE.SERVER,
      match_id: matchId,
      request_id: requestId,
    };

    set(showModalAtom, {
      key: ModalType.AFTER_MATCH_POPUP,
      component: () =>
        AfterMatchModal({
          eventParams,
          onReport: () => controlledPromise.resolve('REPORT'),
          onClose: () => controlledPromise.resolve('CANCEL'),
        }),
    });

    const action = await controlledPromise.promise;
    set(closeModalAtom, ModalType.AFTER_MATCH_POPUP);

    if (captureData.source === 'hvm') {
      set(
        MLMetricsAtom,
        get(MLMetricsAtom).concat([
          {
            triggeredAt: captureData.triggeredAt,
            action: action === 'REPORT' ? 'reported' : 'dismissed',
            feature: MLFeatureType.AfterMatchHVReportPopupMinor,
            modelType: MLModelType.HumanVision,
            actedAt: Date.now(),
          },
        ])
      );
    }

    if (action === 'REPORT') {
      try {
        if (captureData.source === 'hvs') {
          await ReportMatchAPI({
            matchId,
            reportType: 'CHILD_AGE_ABUSE',
            reportMethod: ReportMatchMethod.AFTER_MATCH_REPORT_POPUP_USER,
            fileInfo: captureData.fileInfo,
          });
        } else {
          await set(validateImageAndReportMatchAtom, {
            file: captureData.file,
            matchId,
            reportType: 'CHILD_AGE_ABUSE',
            reportMethod: ReportMatchMethod.AFTER_MATCH_REPORT_POPUP_USER,
          });
        }
        set(showToastAtom, { message: 'COMMON__REPORT__SENT', type: ToastType.SUCCESS });
      } catch (error) {
        Sentry.captureMessage('Failed to report match at AfterMatchPopup', {
          extra: { error, matchId, requestId, source: captureData.source },
        });
      }
    }
  }
);
